<template>
  <div class="gift-giver-registry-page">
    <button v-if="searchToGiftGiverPage" class="back-button margin-top-s grid-x" @click="backToSearchPage">
      <span class="swipe-arrow-left margin-right-xxs"></span><span class="backToSearchButton medium">Back</span>
    </button>
    <div v-if="giftGiverViewLoading">
      <HeaderBannerLoader :is-registrant="false" />
      <RegistrantMyGiftLoader />
      <BarCodeLoader />
    </div>
    <div v-else>
      <div v-if="!showErrorState">
        <HeaderBanner :header-banner-data="headerBanner" :class="{ 'header-container': products.length > 0 }" />
        <div v-if="products.length > 0">
          <RegistryUiFilteringFacets v-if="products.length > 0" :facets="facets" :sort-by="sortBy" @selected-criteria="filtering" />

          <div id="product-card-container" class="grid-x grid-margin-x product-card-container">
            <div
              v-for="(product, index) in productsPaginated"
              :key="product.keyId"
              class="cell small-6 medium-4 large-3 large-margin-bottom-s large-margin-top-s small-margin-bottom-xs"
            >
              <GiftCardProductCard
                v-if="product.isGiftCard"
                :product-item="product"
                :is-registrant="false"
                @open-gif-card-slideout="toggleProductInfoSlideout"
              />
              <ProductCard
                v-else-if="!product.dreamFundFlag"
                :product-item="product"
                :is-registrant="false"
                :is-add-to-bag-loading="quickViewStore.gridLoadingProductId === product.id && product.keyId === targetKeyId"
                :image-priority="index < 4"
                @toggle-add-to-bag-slideout="handleAddToBag"
                @toggle-product-info-slideout="toggleProductInfoSlideout"
              />

              <DreamFundProductCard v-else :product-item="product" :image-width="241" image-aspect-ratio="0.8" />
            </div>
            <div v-if="!isInfiniteScrollEnabled && paginationFlag" class="fadeInPagination">
              <AtomicUiPagination
                :key="paginationKey"
                :total="filteredProductItems.length"
                :default-page-size="20"
                :page-size-options="[20, 40]"
                alignment="space-between"
                class="registry-pagination cell margin-top-s"
                @pagination-page-select="handlePageChange"
              />
            </div>
          </div>

          <BarCode
            v-if="!giftGiverViewLoading && !showErrorState"
            :barcode-number="registryBarCode"
            :hide-text="true"
            :is-baby-registry="isBabyRegistry"
            class="large-margin-top-xxl"
          />
        </div>
        <div v-else class="padding-bottom-xxxl">
          <div class="no-product-available cell text-center large-padding-l small-padding-left-xs small-padding-right-xs small-padding-l">
            <div class="margin-bottom-s">
              <span class="registry-box"></span>
            </div>
            <div class="grid-x grid-margin-x">
              <div class="large-5 text-content">
                <div class="large margin-bottom-xs">
                  <div v-html="giftGiverRegistryView.noItemsError"></div>
                </div>
                <div class="large">Check back soon!</div>
              </div>
            </div>
          </div>
        </div>
        <component :is="ATBComponent" :registry-type="registryType" :show-header-details="showHeaderDetails" @atb-closed="handleAtbClosed" />
        <AtomicUiSlideout :id="GIFT_GIVER_PRODUCT_SLIDEOUT_ID" title="" position="right" @close-slideout="closeSlideout">
          <QuickView
            v-if="selectedProduct && !selectedProduct.isGiftCard"
            :product="selectedProduct"
            :product-id="selectedProduct.id"
            :upc-id="selectedProduct.detail?.upcId"
            :quantity-requested="selectedProduct.purchased?.quantity"
            :quantity-purchased="selectedProduct.purchased?.sold"
          />

          <template #footer>
            <QuickViewFooter
              v-if="selectedProduct"
              :product="selectedProduct"
              :quantity-requested="selectedProduct.purchased?.quantity"
              :quantity-purchased="selectedProduct.purchased?.sold"
              :slideout-id="GIFT_GIVER_PRODUCT_SLIDEOUT_ID"
              :is-registrant="false"
            />
          </template>
        </AtomicUiSlideout>
        <AtomicUiSlideout :id="GIFT_GIVER_GIFT_CARD_SLIDEOUT_ID" title="" position="right" @close-slideout="closeGiftCardSlideout">
          <GiftGiverGiftCard
            v-if="selectedProduct"
            :product="selectedProduct"
            :e-gift-enabled="headerBanner.egiftCardEnabled"
            :physical-gift-card-enabled="headerBanner.physicalGiftCardEnabled"
          />

          <template #footer>
            <GiftCardFooter
              v-if="selectedProduct"
              :product="selectedProduct"
              :e-gift-enabled="headerBanner.egiftCardEnabled"
              :slideout-id="GIFT_GIVER_GIFT_CARD_SLIDEOUT_ID"
              @registry-atb-error="handleGiftGiverAtbError"
              @refresh-atb="handleRefresh"
            />
          </template>
        </AtomicUiSlideout>
        <AtomicUiSlideout :id="GIFT_GIVER_ADD_TO_BAG_SLIDEOUT_ID" title="" position="right">
          <AtomicUiNotification
            v-if="errorMessage || errorMessages.addToBagError"
            :key="errorMessage"
            severity="error"
            class="cell"
            :closable="false"
          >
            {{ errorMessage || errorMessages.addToBagError }}
            <br />
            <AtomicUiButton class="link-body" variant="link" @click="handleRefresh"> Refresh </AtomicUiButton>
          </AtomicUiNotification>

          <AtomicUiButton class="link-body large-margin-top-s" variant="link" @click="handleBackToRegistry">
            {{ giftGiverRegistryView.backToRegistry }}
          </AtomicUiButton>
        </AtomicUiSlideout>
      </div>
      <div v-else>
        <div class="error-state margin-bottom-xxxl large-margin-top-xxl margin-top-xl">
          <div class="grid-x align-center cell">
            <span class="error-filled margin-top-xxl"></span>
          </div>
          <div class="grid-x align-center text-center">
            <div class="large margin-top-s margin-bottom-xxl large-4">
              {{ giftGiverApiErrorResponse?.[0]?.description || errorMessages.giftGiverView }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import useAddToBag from '~/composables/useAddToBag';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { warn, setupLogLevels } from '@nuxt-common/util/app/utils/universalLogger';
import { GET_REGISTRY_INFO } from '~/types/actions';
import {
  SET_UPC_DETAILS,
  SET_QUICK_VIEW_PRODUCT_DETAILS,
  SET_GIFTGIVER_TO_SEARCHPAGE,
  SET_SEARCH_TO_GIFTGIVERPAGE,
  SET_ATB_COMPONENT,
  SET_IN_VALID_GIFT_CARD_AMOUNT,
  SET_ATB_LOADING_STATE,
  SET_GIFT_CARD_AMOUNT,
} from '~/types/mutations';
import { transformProducts } from '~/utils/giftTransformProduct';
import ProductCard from '~/components/ProductCard.vue';
import GiftCardProductCard from '~/components/GiftCardProductCard.vue';
import DreamFundProductCard from '~/components/DreamFundProductCard.vue';
import HeaderBanner from '~/components/HeaderBanner.vue';
import HeaderBannerLoader from '~/components/HeaderBannerLoader.vue';

import BarCode from '~/common/BarCode.vue';
import { paginate, registrantFormatDate, infiniteScrollPaginate } from '~/utils/SearchRegistryUtil';
import RegistrantMyGiftLoader from '~/components/RegistrantMyGiftLoader.vue';
import BarCodeLoader from '~/components/BarCodeLoader.vue';
import { errorMessages, giftGiverRegistryView, setRegistryFor } from 'config';
import { transformFilters, transformFilterContent } from '~/utils/FilteringFacetsUtil';

import { useEnvPropsStore } from '~/stores/envProps';
import { useGiftGiverRegistryView } from '~/stores/giftGiverRegistryView';
import { useSearchRegistryStore } from '~/stores/searchRegistry';
import { useQuickViewStore } from '~/stores/quickView';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';

/* eslint-disable @typescript-eslint/no-unused-vars */
const QuickView = defineAsyncComponent(() => import('~/components/QuickView.vue'));
const QuickViewFooter = defineAsyncComponent(() => import('~/components/QuickViewFooter.vue'));
const GiftGiverGiftCard = defineAsyncComponent(() => import('~/components/GiftGiverGiftCard.vue'));
const GiftCardFooter = defineAsyncComponent(() => import('~/components/GiftCardFooter.vue'));
/* eslint-enable @typescript-eslint/no-unused-vars */

const GIFT_GIVER_GIFT_CARD_SLIDEOUT_ID = 'gift-giver-gift-card';
const GIFT_GIVER_ADD_TO_BAG_SLIDEOUT_ID = 'gift-giver-add-to-bag';
const GIFT_GIVER_PRODUCT_SLIDEOUT_ID = 'gift-giver-product';

const route = useRoute();
const router = useRouter();

const envPropsStore = useEnvPropsStore();
const giftGiverRegistryViewStore = useGiftGiverRegistryView();
const searchRegistryStore = useSearchRegistryStore();
const slideoutStore = useSlideoutStore();
const quickViewStore = useQuickViewStore();

const selectedProduct = ref({}); // Changed initialization to an empty object
const filteredProductItems = ref([]);
const filterOrder = ref(['CATEGORY', 'STATUS', 'PRICE']);
const sortBy = ref([
  {
    value: 'RECENTLY_ADDED',
    displayValue: giftGiverRegistryView.sortBy.recentlyAdded,
  },
  {
    value: 'PRICE_LOW_TO_HIGH',
    displayValue: giftGiverRegistryView.sortBy.priceLowToHigh,
  },
  {
    value: 'PRICE_HIGH_TO_LOW',
    displayValue: giftGiverRegistryView.sortBy.priceHighToLow,
  },
]);
const paginationKey = ref(1);
const productsPaginated = ref([]);
const infiniteScrollPageSize = ref(1);
const isInfiniteScrollEnabled = ref(false);

const sourceSystem = computed(() => envPropsStore.envProps.entryPoint.toUpperCase());
const isBcom = computed(() => sourceSystem.value === 'BCOM');
const orgId = computed(() => (isBcom.value ? 13 : 12));
const brandName = computed(() => (isBcom.value ? 'Bloomingdale’s' : 'Macy’s'));
const showHeaderDetails = computed(() => (quickViewStore.atbSource === 'GRID' ? !isBcom.value : true));
const registryId = computed(() => route.params.registryId);

const registry = computed(() => giftGiverRegistryViewStore.registryInfo);
const searchToGiftGiverPage = computed(() => searchRegistryStore.searchToGiftGiverPage);
const registryGUID = computed(() => searchRegistryStore.selectedRegistryGuid);
const products = computed(() => transformProducts(registry.value?.registryItems, 'GIFT_GIVER'));
const facets = computed(() => transformFilters(registry.value?.filter, filterOrder.value, orgId.value));
const giftGiverApiErrorResponse = computed(() => giftGiverRegistryViewStore.giftGiverApiErrorResponse);
const registryType = computed(() => registry.value?.occasionType?.toUpperCase());
const isBabyRegistry = computed(() => registryType.value === 'BABY');
const { addItemToBag } = useAddToBag();

const registrantFirstName = computed(() => decodeURIComponent(route.params.registrantFirstName || ''));
const registrantLastName = computed(() => decodeURIComponent(route.params.registrantLastName || ''));
const coregistrantFirstName = computed(() => decodeURIComponent(route.params.coregistrantFirstName || ''));
const coregistrantLastName = computed(() => decodeURIComponent(route.params.coregistrantLastName || ''));

useHead({
  title: () => {
    const primary = [registrantFirstName.value, registrantLastName.value].filter(Boolean).join(' ');
    const co = [coregistrantFirstName.value, coregistrantLastName.value].filter(Boolean).join(' ');

    if (primary && co) {
      return `View ${primary} and ${co}’s Registry at ${brandName.value}`;
    }
    if (primary) {
      return `View ${primary}’s Registry at ${brandName.value}`;
    }
    // Fallback if no names available
    return `View Registry at ${brandName.value}`;
  },
});
const handleBackToRegistry = async () => {
  slideoutStore.toggle(GIFT_GIVER_ADD_TO_BAG_SLIDEOUT_ID, { root: true });
  slideoutStore.close(GIFT_GIVER_GIFT_CARD_SLIDEOUT_ID, { root: true });
};

const handleRefresh = async () => {
  // Close the currently open slideout
  slideoutStore.toggle(GIFT_GIVER_ADD_TO_BAG_SLIDEOUT_ID, { root: true });

  // Trigger the onAddToBag function again for the selected product
  if (selectedProduct.value) {
    await onAddToBag(selectedProduct.value, selectedProduct.value.quantity || 1);
  }
};

watch(
  () => isBabyRegistry.value,
  (isBaby) => {
    setRegistryFor(isBaby);
  },
  { immediate: true },
);

const ATBComponent = computed(() => quickViewStore.ATBComponent);
const handleAtbClosed = () => {
  quickViewStore[SET_ATB_COMPONENT](null);
};
const errorMessage = ref(null);
const handleGiftGiverAtbError = (msg) => {
  errorMessage.value = msg;
  slideoutStore.toggle(GIFT_GIVER_ADD_TO_BAG_SLIDEOUT_ID, { root: true });
};

const getRegistryInfo = async () => {
  await giftGiverRegistryViewStore[GET_REGISTRY_INFO]({
    registryId: registryId.value,
    registryGUID: searchToGiftGiverPage.value ? registryGUID.value : '',
    orgId: orgId.value,
  });

  if (giftGiverApiErrorResponse.value) {
    fireTag('link', {
      event_name: 'Registry error',
      error_category: 'registry',
      error_code: [`REG${giftGiverApiErrorResponse.value?.[0]?.code || '999'}`],
      error_label: [' '],
      error_message: [giftGiverApiErrorResponse.value?.[0]?.description || errorMessages.giftGiverView],
      error_misc: [' '],
      error_scope: ['Global'],
    });
  }
};

const backToSearchPage = () => {
  searchRegistryStore[SET_GIFTGIVER_TO_SEARCHPAGE](true);
  searchRegistryStore[SET_SEARCH_TO_GIFTGIVERPAGE](false);
  router.push('/registry/search');
};

const toggleProductInfoSlideout = (product) => {
  selectedProduct.value = product;
  if (product.isGiftCard) {
    slideoutStore.toggle(GIFT_GIVER_GIFT_CARD_SLIDEOUT_ID);
    fireTag('view', {
      page_type: 'registry',
      page_name: 'registry gift card details',
    });
  } else {
    slideoutStore.toggle(GIFT_GIVER_PRODUCT_SLIDEOUT_ID);
  }
};

const onAddToBag = (product, selectedQuantity) => {
  selectedProduct.value = {
    ...product,
    quantity: selectedQuantity,
  };
  targetKeyId.value = product.keyId;
  quickViewStore[SET_ATB_LOADING_STATE]({ source: 'GRID', productId: product.id });
  const payload = {
    item: {
      upcId: product.detail.upcId,
      upcNumber: product.detail.upcNumber,
      registryId: registryId.value,
      source: 'REGA2B',
      quantity: selectedQuantity,
    },
  };
  return addItemToBag(payload, { useDirectLink: true })
    .then((atbConfirmation) => {
      quickViewStore[SET_ATB_COMPONENT](atbConfirmation.component);
    })
    .catch((error) => {
      const message = error?.cause?.message || error?.message;
      errorMessage.value = message;
      slideoutStore.toggle(GIFT_GIVER_ADD_TO_BAG_SLIDEOUT_ID);
    })
    .finally(() => {
      quickViewStore[SET_ATB_LOADING_STATE]({ source: 'GRID', productId: null });
      targetKeyId.value = null;
    });
};

const handleAddToBag = async (product, selectedQuantity) => {
  if (product.isGiftCard) {
    selectedProduct.value = product;
    slideoutStore.toggle(GIFT_GIVER_GIFT_CARD_SLIDEOUT_ID);
    return;
  }
  onAddToBag(product, selectedQuantity);
};

const handlePageChange = (pageSize, pageNumber, change) => {
  if (!change) {
    window.scrollTo(0, 0);
  }
  productsPaginated.value = paginate(filteredProductItems.value, pageSize, pageNumber);
};
const facetsValue = ref([]);
const sortByValue = ref(sortBy.value[0].value);
const targetKeyId = ref(null);
const filtering = (selected) => {
  const sortedFacet = selected?.allSelected?.facets
    .map((e) => e.selectedValues)
    .flat()
    .sort();
  const sortedSortBy = selected?.allSelected?.sortBy;
  if (JSON.stringify(facetsValue.value) !== JSON.stringify(sortedFacet) && sortedFacet.length >= 1) {
    fireTag('link', {
      event_name: 'registry details: facet click',
      custom_link_name: 'registry details: facet click',
      registry_facet: sortedFacet,
    });
    facetsValue.value = sortedFacet;
  }
  if (sortByValue.value !== sortedSortBy) {
    fireTag('link', {
      event_name: 'registry details: sort by click',
      custom_link_name: 'registry details: sort by click',
      registry_sort_by: sortedSortBy.split('_').join(' ').toLowerCase(),
    });
    sortByValue.value = sortedSortBy;
  }

  const { filteredContent } = transformFilterContent(selected, products.value);
  filteredProductItems.value = filteredContent;
};

const isInfiniteScrollExpEnabled = async () => {
  try {
    const { Experimentation } = await import('@nuxt-common/util/app/utils/experimentation');
    const selectedRecipes = await Experimentation.getExpIds();
    return selectedRecipes.includes(giftGiverRegistryView.infiniteScrollExpId);
  } catch (e) {
    setupLogLevels('warn');
    warn('Issue while loading Experimentation -', e);
  }

  return false;
};

let lastScrollTop = 0;
let scrollTicking = false;
const paginationFlag = ref(false);
const handleInfiniteScrollFrame = () => {
  scrollTicking = false;
  const element = document.querySelector('#product-card-container');
  const productCard = document.querySelector('.product-card');
  if (!element || !productCard) return;
  const scrollTop = Math.floor(window.pageYOffset || document.documentElement.scrollTop);
  const scrollHeight = Math.floor(element.scrollHeight);
  const productCardHeight = Math.floor(productCard.offsetHeight);
  if (scrollTop > lastScrollTop && scrollTop >= scrollHeight - productCardHeight * 2) {
    infiniteScrollPageSize.value += 1;
    productsPaginated.value = infiniteScrollPaginate(productsPaginated.value, filteredProductItems.value, 20, infiniteScrollPageSize.value);
  }
  lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
};
const handleInfiniteScroll = () => {
  if (scrollTicking) return;
  scrollTicking = true;
  requestAnimationFrame(handleInfiniteScrollFrame);
};

watch(
  () => products.value,
  () => {
    const { filteredContent } = transformFilterContent(null, products.value);
    filteredProductItems.value = filteredContent;
  },
);

watch(
  () => filteredProductItems.value,
  () => {
    paginationFlag.value = false;
    isInfiniteScrollEnabled.value = false;
    window.removeEventListener('scroll', handleInfiniteScroll);
    infiniteScrollPageSize.value = 1;

    paginationKey.value += 1;
    handlePageChange(20, 1, true);

    isInfiniteScrollExpEnabled()
      .then((resp) => {
        if (resp) {
          isInfiniteScrollEnabled.value = true;
          productsPaginated.value = infiniteScrollPaginate([], filteredProductItems.value, 20, infiniteScrollPageSize.value);
          nextTick(() => {
            window.addEventListener('scroll', handleInfiniteScroll, { passive: true });
          });
        }
      })
      .catch(() => {});
    nextTick(() => {
      paginationFlag.value = true;
    });
  },
);

watch(
  () => registry.value,
  () => {
    onLoadFiretag();
  },
);

const headerBanner = computed(() => registry.value);
const registryBarCode = computed(() => giftGiverRegistryViewStore.registryBarCode);

const closeSlideout = () => {
  selectedProduct.value = null;
  quickViewStore[SET_UPC_DETAILS]({});
  quickViewStore[SET_QUICK_VIEW_PRODUCT_DETAILS](null);
};

const closeGiftCardSlideout = () => {
  quickViewStore[SET_GIFT_CARD_AMOUNT]('');
  quickViewStore[SET_IN_VALID_GIFT_CARD_AMOUNT](true);
  closeSlideout();
};

onMounted(() => {
  if (registryId.value) {
    getRegistryInfo();
  }

  import('@nuxt-common/util/app/utils/experimentation')
    .then(({ ExperimentationTags }) => ExperimentationTags.fireAdobeTags())
    .catch((e) => {
      setupLogLevels('warn');
      warn('Issue while loading ExperimentationTags -', e);
    });
});

const giftGiverViewLoading = computed(() => giftGiverRegistryViewStore.giftGiverViewLoading);
const showErrorState = computed(() => giftGiverRegistryViewStore.error);

const onLoadFiretag = () => {
  fireTag('view', {
    page_name: 'registry gift giver',
    page_type: 'registry',
    registry_id: registryId.value?.toString().split('&')[0],
    registry_view_type: 'gift giver',
    registry_type: registry.value?.occasionType?.toLowerCase(),
    registry_event_date: registrantFormatDate(registry.value?.occasionDate),
    registry_reach_scope: registry.value?.privacyPreference,
  });
};

onBeforeUnmount(() => window.removeEventListener('scroll', handleInfiniteScroll));
</script>

<style lang="scss" scoped>
@import '@csgIcons/_registry-box';
@import '@csgIcons/_error-filled.scss';
@import '~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_swipe-arrow-left.scss';

.back-button {
  cursor: pointer;
  align-items: flex-start;
}

.backToSearchButton {
  margin-top: rem-calc(2);
}

.gift-giver-registry-page {
  position: inherit;

  @include breakpoint(small down) {
    .header-container > :deep(div) {
      @include padding-direction('bottom', map-get($spacer, 'xxs'));
    }
  }
}

:deep(.filtering-facets) {
  height: auto !important;

  @include breakpoint(medium up) {
    padding-top: rem-calc(6);
    padding-bottom: 0;
  }

  @include breakpoint(small down) {
    @include padding-direction('top', map-get($spacer, 's'));
    @include padding-direction('bottom', map-get($spacer, 's'));
  }
}

:deep(.facet-container) {
  &::-webkit-scrollbar {
    height: 0;
  }
  @include margin-direction('top', map-get($spacer, 'xs'));
}

.product-card-container {
  margin-left: 0;
  margin-right: 0;

  @include breakpoint(small down) {
    margin-top: 0;
  }

  @include breakpoint(medium up) {
    @include margin-direction('top', map-get($spacer, 's'));
  }
}

@include breakpoint(medium up) {
  :deep(.pagination-wrap) {
    justify-content: space-between;

    .filtered-pagination {
      width: 55.55%;
      justify-content: end;
    }
  }
}

@include breakpoint(small down) {
  :deep(.pagination-wrap) {
    justify-content: center;

    .filtered-pagination {
      display: none;
    }
  }
}

.error-state {
  background-color: $background-color;
}

.no-product-available {
  background-color: $background-color;
}

.registry-box {
  width: rem-calc(40);
  height: rem-calc(34);
  background-size: rem-calc(40) rem-calc(34);
}

.text-content {
  margin: 0 auto;
}

.error-filled {
  width: rem-calc(36);
  height: rem-calc(36);
}
.fadeInPagination {
  width: 100%;
  animation: fadeIn 0.5s ease-in;
}
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
